import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  Play,
  Loader2,
  MousePointer2,
  MessageSquarePlus,
  X,
} from 'lucide-react';
import axios from 'axios';

const authHeaders = () => {
  const token = localStorage.getItem('authToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function ticketFromPreviewPath(previewPath) {
  const m = String(previewPath || '').match(/\/preview\/([a-f0-9]{16,})(?:\/|$)/i);
  return m ? m[1] : '';
}

/** Adressleiste: absolute localhost/Vite-URLs → relativer Pfad (nie /http://…) */
function normalizeLivePath(raw) {
  let s = String(raw || '').trim();
  if (!s) return '/';

  s = s.replace(/^\/+(https?:\/\/)/i, '$1');

  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const path = `${u.pathname || '/'}${u.search || ''}${u.hash || ''}` || '/';
      const previewIdx = path.indexOf('/preview/');
      if (previewIdx >= 0) {
        const afterTicket = path.slice(previewIdx).replace(/^\/preview\/[^/]+/, '');
        const rest = afterTicket.replace(/^\/+/, '/') || '/';
        return rest.startsWith('/') ? rest : `/${rest}`;
      }
      return path.startsWith('/') ? path : `/${path}`;
    } catch (_) {
      return '/';
    }
  }

  if (/^\/?https?:/i.test(s) || /localhost:\d+/i.test(s) || /127\.0\.0\.1:\d+/i.test(s)) {
    try {
      const withScheme = /^https?:/i.test(s) ? s.replace(/^\//, '') : `http://${s.replace(/^\/+/, '')}`;
      return normalizeLivePath(withScheme);
    } catch (_) {
      return '/';
    }
  }

  if (!s.startsWith('/')) s = `/${s}`;
  s = s.replace(/\/{2,}/g, '/');
  return s || '/';
}

function escapeCssIdent(value) {
  const s = String(value || '');
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
  return s.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, '\\$1');
}

/** Stabiler CSS-Pfad für das markierte Element */
function buildCssPath(el) {
  if (!el || el.nodeType !== 1) return '';
  if (el.id) return `#${escapeCssIdent(el.id)}`;
  const parts = [];
  let node = el;
  let depth = 0;
  while (node && node.nodeType === 1 && node !== node.ownerDocument?.documentElement && depth < 6) {
    let part = String(node.tagName || '').toLowerCase();
    if (!part) break;
    if (node.id) {
      parts.unshift(`#${escapeCssIdent(node.id)}`);
      break;
    }
    const allCls = Array.from(node.classList || []).filter(
      (c) => c && !c.startsWith('__odium')
    );
    const meaningful = allCls.filter((c) => !isUtilityClass(c));
    const cls = (meaningful.length ? meaningful : allCls).slice(0, 2).map(escapeCssIdent);
    if (cls.length) part += `.${cls.join('.')}`;
    const parent = node.parentElement;
    if (parent) {
      const same = Array.from(parent.children).filter(
        (c) => c.tagName === node.tagName
      );
      if (same.length > 1) {
        part += `:nth-of-type(${same.indexOf(node) + 1})`;
      }
    }
    parts.unshift(part);
    node = parent;
    depth += 1;
  }
  return parts.join(' > ');
}

function interestingAttrs(el) {
  if (!el?.attributes) return {};
  const keep = {};
  for (const attr of Array.from(el.attributes)) {
    const name = String(attr.name || '').toLowerCase();
    if (
      name === 'id' ||
      name === 'class' ||
      name === 'name' ||
      name === 'type' ||
      name === 'href' ||
      name === 'src' ||
      name === 'alt' ||
      name === 'title' ||
      name === 'role' ||
      name === 'placeholder' ||
      name.startsWith('aria-') ||
      name.startsWith('data-')
    ) {
      keep[attr.name] = String(attr.value || '').slice(0, 200);
    }
  }
  return keep;
}

/** Tailwind-/Layout-Utilities – ungeeignet als Mention-Label */
function isUtilityClass(c) {
  return /^(flex|grid|block|inline|inline-flex|inline-block|hidden|relative|absolute|fixed|sticky|static|w-|h-|min-|max-|items-|justify-|content-|self-|gap-|space-|p-|px-|py-|pt-|pb-|pl-|pr-|m-|mx-|my-|mt-|mb-|ml-|mr-|text-|font-|leading-|tracking-|bg-|dark:|hover:|focus:|active:|group|peer|sm:|md:|lg:|xl:|2xl:|rounded|shadow|border|ring|outline|overflow|container|transition|duration-|ease-|delay-|scale-|rotate-|translate-|opacity-|cursor-|pointer-events-|select-|object-|z-|order-|col-|row-|basis-|grow|shrink|sr-only|truncate|whitespace-|break-|animate-|from-|via-|to-|ring-|decoration-|underline|antialiased)$/i.test(
    String(c || '')
  ) || /^(w|h|min-w|min-h|max-w|max-h|p|px|py|pt|pb|pl|pr|m|mx|my|mt|mb|ml|mr|gap|space-[xy]|text|font|leading|tracking|bg|rounded|shadow|border|ring|opacity|z|top|right|bottom|left|inset|translate|scale|rotate|skew|duration|delay|ease|transition)(-|$)/i.test(
    String(c || '')
  );
}

function pickMeaningfulClass(className) {
  const classes = String(className || '')
    .split(/\s+/)
    .filter(Boolean)
    .filter((c) => !c.startsWith('__odium'));
  return classes.find((c) => !isUtilityClass(c)) || '';
}

function describeElement(el, pagePath) {
  const tag = String(el.tagName || '').toLowerCase();
  const selector = buildCssPath(el);
  const attrs = interestingAttrs(el);
  let styles = {};
  try {
    const cs = el.ownerDocument?.defaultView?.getComputedStyle?.(el);
    if (cs) {
      styles = {
        display: cs.display,
        position: cs.position,
        color: cs.color,
        backgroundColor: cs.backgroundColor,
        fontSize: cs.fontSize,
        fontWeight: cs.fontWeight,
        width: cs.width,
        height: cs.height,
        padding: cs.padding,
        margin: cs.margin,
      };
    }
  } catch (_) {
    /* ignore */
  }
  let rect = null;
  try {
    const r = el.getBoundingClientRect();
    rect = {
      x: Math.round(r.x),
      y: Math.round(r.y),
      w: Math.round(r.width),
      h: Math.round(r.height),
    };
  } catch (_) {
    /* ignore */
  }
  const text = String(el.innerText || el.textContent || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 400);
  const visibleText = text.slice(0, 80);
  const outerHtml = String(el.outerHTML || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 2500);

  // Label: sichtbarer Text zuerst (sonst weiß niemand, welcher Button) – nicht transition-all
  const aria = attrs['aria-label'] ? String(attrs['aria-label']).trim().slice(0, 40) : '';
  const shortText =
    visibleText && visibleText.length >= 1 && visibleText.length <= 48 && !/[{}<>]/.test(visibleText)
      ? visibleText
      : '';
  const meaningfulCls = pickMeaningfulClass(attrs.class || '');
  const label =
    (aria ? `${tag} „${aria}“` : '') ||
    (shortText ? `${tag} „${shortText}“` : '') ||
    (attrs.id ? `#${attrs.id}` : '') ||
    (meaningfulCls ? `${tag}.${meaningfulCls}` : '') ||
    tag ||
    'element';

  const blob = [
    `Live-UI Element (Nutzer hat in der Vorschau markiert)`,
    `Seite: ${pagePath || '/'}`,
    `Selector: ${selector || tag}`,
    `Tag: <${tag}>`,
    `Kurzname: ${label}`,
    visibleText ? `Sichtbarer Text: ${visibleText}` : null,
    visibleText
      ? `EMPFOHLENE SUCHE: dateien_suchen query="${visibleText.slice(0, 48)}" — NICHT nach Utility-Klassen wie transition-all / w-full / py-3 suchen`
      : `EMPFOHLENE SUCHE: dateien_suchen nach eindeutigem Text/Attribut aus dem HTML — NICHT nach Tailwind-Utilities`,
    Object.keys(attrs).length
      ? `Attribute: ${JSON.stringify(attrs)}`
      : null,
    rect ? `Position: ${rect.w}×${rect.h} @ (${rect.x},${rect.y})` : null,
    Object.keys(styles).length
      ? `Computed Styles: ${JSON.stringify(styles)}`
      : null,
    outerHtml ? `HTML:\n${outerHtml}` : null,
    'Sofort: dateien_suchen mit dem sichtbaren Text → datei_lesen mit offset an der Trefferzeile → datei_aendern. Kein Raten „React oder Vue“.',
  ]
    .filter(Boolean)
    .join('\n');

  return {
    id: `dom-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
    kind: 'dom',
    path: `live:${pagePath || '/'}`,
    selector: selector || tag,
    label,
    visibleText,
    pagePath: pagePath || '/',
    startLine: 1,
    endLine: 1,
    text: blob.slice(0, 12000),
    outerHtml,
  };
}

function emitAddCodeToChat(ref) {
  try {
    window.dispatchEvent(new CustomEvent('odium:agent-add-code-ref', { detail: ref }));
  } catch (_) {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent('odium:agent-open-chat'));
  } catch (_) {
    /* ignore */
  }
}

const AgentLiveView = ({ projectId, refreshKey = 0 }) => {
  const iframeRef = useRef(null);
  const startingRef = useRef(false);
  const ticketRef = useRef('');
  const pickModeRef = useRef(false);
  const pathRef = useRef('/');
  const [path, setPath] = useState('/');
  const [inputPath, setInputPath] = useState('/');
  const [history, setHistory] = useState(['/']);
  const [histIdx, setHistIdx] = useState(0);
  const [starting, setStarting] = useState(false);
  const [mode, setMode] = useState('');
  const [ticket, setTicket] = useState('');
  const [error, setError] = useState('');
  const [frameKey, setFrameKey] = useState(0);
  const [pickMode, setPickMode] = useState(false);
  const [hoverLabel, setHoverLabel] = useState('');
  const [pickFlash, setPickFlash] = useState(false);

  ticketRef.current = ticket;
  pickModeRef.current = pickMode;
  pathRef.current = path;

  const previewBase =
    projectId && ticket
      ? `/api/ai/projects/${encodeURIComponent(projectId)}/preview/${ticket}`
      : '';

  const buildSrc = (p) => {
    if (!previewBase) return 'about:blank';
    const clean = normalizeLivePath(p);
    return `${previewBase}${clean === '/' ? '/' : clean}`;
  };

  const applyStartResult = (data, { resetNav = true, reloadFrame = true } = {}) => {
    const nextTicket = String(data?.ticket || ticketFromPreviewPath(data?.previewPath) || '');
    const ticketChanged = nextTicket && nextTicket !== ticketRef.current;
    setMode(data?.mode || 'static');
    if (nextTicket) setTicket(nextTicket);
    if (resetNav) {
      setPath('/');
      setInputPath('/');
      setHistory(['/']);
      setHistIdx(0);
    }
    if (reloadFrame && (ticketChanged || !ticketRef.current)) {
      setFrameKey((k) => k + 1);
    } else if (reloadFrame && nextTicket) {
      setFrameKey((k) => k + 1);
    }
    const warnParts = [];
    if (data?.bootstrap?.message) warnParts.push(String(data.bootstrap.message));
    if (data?.error) warnParts.push(String(data.error));
    else if (data?.fallback) warnParts.push('Node/Vite-Start fehlgeschlagen — Static-Fallback.');
    if (data?.mode === 'static' && !data?.error) {
      warnParts.push('Static-Modus: nur HTML/CSS/JS ohne Bundler. React/JSX braucht Vite (mode=node).');
    }
    setError(warnParts.length ? warnParts.join(' · ') : '');
    if (!nextTicket) {
      setError((prev) => prev || 'Kein Preview-Ticket erhalten');
    }
  };

  const start = async ({ forceReload = true } = {}) => {
    if (!projectId || startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setError('');
    try {
      const res = await axios.post(
        `/api/ai/projects/${encodeURIComponent(projectId)}/preview/start`,
        {},
        { headers: authHeaders() }
      );
      applyStartResult(res.data, {
        resetNav: true,
        reloadFrame: forceReload,
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Preview Start fehlgeschlagen');
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  };

  useEffect(() => {
    if (!projectId) {
      setTicket('');
      return;
    }
    start({ forceReload: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!projectId || refreshKey === 0) return;
    if (ticketRef.current) {
      setFrameKey((k) => k + 1);
    } else {
      start({ forceReload: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    const onFocus = (ev) => {
      const d = ev?.detail || {};
      if (!d.ticket && !d.previewPath) return;
      const nextTicket = String(d.ticket || ticketFromPreviewPath(d.previewPath) || '');
      if (!nextTicket) return;
      if (nextTicket !== ticketRef.current) {
        setTicket(nextTicket);
        if (d.mode) setMode(String(d.mode));
        setError('');
        setFrameKey((k) => k + 1);
      } else {
        setFrameKey((k) => k + 1);
      }
    };
    window.addEventListener('odium:agent-workspace', onFocus);
    return () => window.removeEventListener('odium:agent-workspace', onFocus);
  }, []);

  const navigate = (nextPath, { push = true } = {}) => {
    const clean = normalizeLivePath(nextPath);
    setPath(clean);
    setInputPath(clean);
    if (push) {
      setHistory((h) => {
        const sliced = h.slice(0, histIdx + 1);
        sliced.push(clean);
        setHistIdx(sliced.length - 1);
        return sliced;
      });
    }
    setFrameKey((k) => k + 1);
  };

  const goBack = () => {
    if (histIdx <= 0) return;
    const i = histIdx - 1;
    setHistIdx(i);
    const p = normalizeLivePath(history[i]);
    setPath(p);
    setInputPath(p);
    setFrameKey((k) => k + 1);
  };

  const goForward = () => {
    if (histIdx >= history.length - 1) return;
    const i = histIdx + 1;
    setHistIdx(i);
    const p = normalizeLivePath(history[i]);
    setPath(p);
    setInputPath(p);
    setFrameKey((k) => k + 1);
  };

  const onElementPicked = useCallback((el) => {
    if (!el || el.nodeType !== 1) return;
    if (String(el.id || '').startsWith('__odium')) return;
    const ref = describeElement(el, pathRef.current);
    emitAddCodeToChat(ref);
    setPickFlash(true);
    window.setTimeout(() => setPickFlash(false), 900);
    // Nach Auswahl Modus aus — wie typischer Inspect-Klick
    setPickMode(false);
    setHoverLabel('');
  }, []);

  // Element-Picker im iframe (same-origin)
  useEffect(() => {
    if (!pickMode || !ticket) {
      setHoverLabel('');
      return undefined;
    }

    let disposed = false;
    let doc = null;
    let box = null;
    let tip = null;
    let lastEl = null;
    let paintedEl = null;
    let pollId = null;
    let animRaf = null;
    let hasBoxPose = false;
    let boxPose = { top: 0, left: 0, width: 0, height: 0 };
    let boxTarget = { top: 0, left: 0, width: 0, height: 0 };
    let tipLabel = '';

    const applyBoxPose = (pose, label) => {
      if (!box) return;
      const st = box.style;
      st.display = 'block';
      st.top = `${Math.max(0, pose.top)}px`;
      st.left = `${Math.max(0, pose.left)}px`;
      st.width = `${Math.max(0, pose.width)}px`;
      st.height = `${Math.max(0, pose.height)}px`;
      if (tip) {
        tip.textContent = label || tipLabel;
        tip.style.display = 'block';
        tip.style.opacity = '1';
        tip.style.top = `${Math.max(0, pose.top - 22)}px`;
        tip.style.left = `${Math.max(0, pose.left)}px`;
      }
    };

    const stopAnim = () => {
      if (animRaf != null) {
        cancelAnimationFrame(animRaf);
        animRaf = null;
      }
    };

    const animTick = () => {
      animRaf = null;
      if (!box) return;
      // Cursor-ähnlich: schnelles Catch-up, weiches Nachziehen
      const ease = 0.28;
      const next = {
        top: boxPose.top + (boxTarget.top - boxPose.top) * ease,
        left: boxPose.left + (boxTarget.left - boxPose.left) * ease,
        width: boxPose.width + (boxTarget.width - boxPose.width) * ease,
        height: boxPose.height + (boxTarget.height - boxPose.height) * ease,
      };
      const dist =
        Math.abs(next.top - boxTarget.top) +
        Math.abs(next.left - boxTarget.left) +
        Math.abs(next.width - boxTarget.width) +
        Math.abs(next.height - boxTarget.height);
      if (dist < 0.6) {
        boxPose = { ...boxTarget };
        applyBoxPose(boxPose, tipLabel);
        return;
      }
      boxPose = next;
      applyBoxPose(boxPose, tipLabel);
      animRaf = requestAnimationFrame(animTick);
    };

    const teardown = () => {
      stopAnim();
      if (!doc) return;
      try {
        doc.removeEventListener('mousemove', onMove, true);
        doc.removeEventListener('click', onClick, true);
        doc.removeEventListener('keydown', onKey, true);
        doc.removeEventListener('scroll', onScroll, true);
      } catch (_) {
        /* ignore */
      }
      try {
        box?.remove();
        tip?.remove();
      } catch (_) {
        /* ignore */
      }
      try {
        if (doc.documentElement) doc.documentElement.style.cursor = '';
      } catch (_) {
        /* ignore */
      }
      box = null;
      tip = null;
      lastEl = null;
      paintedEl = null;
      hasBoxPose = false;
      doc = null;
    };

    const paint = (el, { animate = true } = {}) => {
      if (!box || !el || !doc) return;
      try {
        const r = el.getBoundingClientRect();
        const nextTarget = {
          top: Math.max(0, r.top),
          left: Math.max(0, r.left),
          width: Math.max(0, r.width),
          height: Math.max(0, r.height),
        };
        const tag = String(el.tagName || '').toLowerCase();
        const txt = String(el.innerText || el.textContent || '')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 36);
        const meaningful = pickMeaningfulClass(
          Array.from(el.classList || []).join(' ')
        );
        const label =
          (txt ? `${tag} „${txt}“` : '') ||
          (el.id ? `#${el.id}` : '') ||
          (meaningful ? `${tag}.${meaningful}` : '') ||
          (el.classList?.length
            ? `${tag}.${Array.from(el.classList).slice(0, 2).join('.')}`
            : tag);
        tipLabel = label;
        boxTarget = nextTarget;
        paintedEl = el;

        if (!animate || !hasBoxPose) {
          stopAnim();
          boxPose = { ...nextTarget };
          hasBoxPose = true;
          applyBoxPose(boxPose, label);
        } else {
          if (animRaf == null) animRaf = requestAnimationFrame(animTick);
        }
        setHoverLabel(label);
      } catch (_) {
        /* ignore */
      }
    };

    const onMove = (e) => {
      if (!pickModeRef.current) return;
      let el = e.target;
      if (!el || el === box || el === tip) return;
      if (el.nodeType !== 1) el = el.parentElement;
      if (!el || el === doc.documentElement || el === doc.body) return;
      if (String(el.id || '').startsWith('__odium')) return;
      // Gleiches Element: Pose nur bei Größenänderung aktualisieren (kein Spam)
      if (el === paintedEl) {
        const r = el.getBoundingClientRect();
        const w = Math.max(0, r.width);
        const h = Math.max(0, r.height);
        if (
          Math.abs(w - boxTarget.width) < 1 &&
          Math.abs(h - boxTarget.height) < 1 &&
          Math.abs(Math.max(0, r.top) - boxTarget.top) < 1 &&
          Math.abs(Math.max(0, r.left) - boxTarget.left) < 1
        ) {
          return;
        }
      }
      lastEl = el;
      paint(el, { animate: true });
    };

    const onScroll = () => {
      if (lastEl) paint(lastEl, { animate: false });
    };

    const onClick = (e) => {
      if (!pickModeRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      let el = e.target;
      if (el === box || el === tip) el = lastEl;
      if (!el || el.nodeType !== 1) return;
      if (String(el.id || '').startsWith('__odium')) return;
      onElementPicked(el);
    };

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setPickMode(false);
      }
    };

    const attach = () => {
      if (disposed) return false;
      const iframe = iframeRef.current;
      if (!iframe) return false;
      let nextDoc = null;
      try {
        nextDoc = iframe.contentDocument;
      } catch (_) {
        return false;
      }
      if (!nextDoc?.body) return false;
      if (doc === nextDoc && box) return true;

      teardown();
      doc = nextDoc;

      box = doc.createElement('div');
      box.id = '__odium_inspect_box';
      Object.assign(box.style, {
        position: 'fixed',
        zIndex: '2147483646',
        pointerEvents: 'none',
        border: '2px solid #38bdf8',
        background: 'rgba(56, 189, 248, 0.12)',
        borderRadius: '4px',
        boxSizing: 'border-box',
        display: 'none',
        margin: '0',
        padding: '0',
        top: '0',
        left: '0',
        width: '0',
        height: '0',
        transition: 'none',
        willChange: 'top, left, width, height',
        boxShadow: '0 0 0 1px rgba(56, 189, 248, 0.25), 0 0 12px rgba(56, 189, 248, 0.15)',
      });

      tip = doc.createElement('div');
      tip.id = '__odium_inspect_tip';
      Object.assign(tip.style, {
        position: 'fixed',
        zIndex: '2147483647',
        pointerEvents: 'none',
        background: '#0c0f14',
        color: '#7dd3fc',
        border: '1px solid rgba(56,189,248,0.45)',
        borderRadius: '4px',
        font: '11px/1.2 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        padding: '2px 6px',
        display: 'none',
        maxWidth: '280px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        top: '0',
        left: '0',
        opacity: '1',
        transition: 'none',
        willChange: 'top, left',
      });

      hasBoxPose = false;
      stopAnim();

      try {
        doc.documentElement.appendChild(box);
        doc.documentElement.appendChild(tip);
        doc.documentElement.style.cursor = 'crosshair';
      } catch (_) {
        return false;
      }

      doc.addEventListener('mousemove', onMove, true);
      doc.addEventListener('click', onClick, true);
      doc.addEventListener('keydown', onKey, true);
      doc.addEventListener('scroll', onScroll, true);
      return true;
    };

    if (!attach()) {
      pollId = window.setInterval(() => {
        if (attach() && pollId) {
          window.clearInterval(pollId);
          pollId = null;
        }
      }, 250);
    }

    const iframe = iframeRef.current;
    const onLoad = () => attach();
    iframe?.addEventListener('load', onLoad);

    return () => {
      disposed = true;
      if (pollId) window.clearInterval(pollId);
      iframe?.removeEventListener('load', onLoad);
      teardown();
      setHoverLabel('');
    };
  }, [pickMode, ticket, frameKey, onElementPicked]);

  if (!projectId) {
    return (
      <div className="h-full flex items-center justify-center text-[12px] text-neutral-500 bg-neutral-950">
        Projekt wählen für Live-View
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-neutral-950 text-neutral-200">
      <div className="flex-shrink-0 flex items-center gap-1 px-1.5 py-1 border-b border-neutral-800 bg-neutral-950">
        <button
          type="button"
          onClick={goBack}
          className="p-1.5 rounded text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
          title="Zurück"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={goForward}
          className="p-1.5 rounded text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
          title="Vor"
        >
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setFrameKey((k) => k + 1)}
          className="p-1.5 rounded text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
          title="Neu laden"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <form
          className="flex-1 min-w-0"
          onSubmit={(e) => {
            e.preventDefault();
            navigate(inputPath);
          }}
        >
          <input
            value={inputPath}
            onChange={(e) => setInputPath(e.target.value)}
            onBlur={() => setInputPath(normalizeLivePath(inputPath))}
            className="w-full text-[11px] py-1 px-2 font-mono rounded-md border border-neutral-700 bg-neutral-900 text-neutral-200 placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
            placeholder="/"
            spellCheck={false}
            title={previewBase ? buildSrc(path) : ''}
          />
        </form>
        <button
          type="button"
          onClick={() => setPickMode((v) => !v)}
          disabled={!ticket}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border transition-colors disabled:opacity-40 ${
            pickMode
              ? pickFlash
                ? 'border-emerald-400/50 bg-emerald-500/25 text-emerald-100'
                : 'border-sky-500/50 bg-sky-500/20 text-sky-100'
              : 'border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
          }`}
          title={
            pickMode
              ? 'Select-Modus beenden (Esc)'
              : 'Element in der Live-Vorschau markieren und zum Chat senden'
          }
          aria-pressed={pickMode}
        >
          {pickMode ? (
            pickFlash ? (
              <MessageSquarePlus className="w-3 h-3" />
            ) : (
              <X className="w-3 h-3" />
            )
          ) : (
            <MousePointer2 className="w-3 h-3" />
          )}
          {pickMode ? (pickFlash ? 'Gesendet' : 'Beenden') : 'Select'}
        </button>
        <button
          type="button"
          onClick={() => start({ forceReload: true })}
          disabled={starting}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-neutral-700 bg-neutral-900 text-neutral-300 hover:bg-neutral-800"
          title="Preview starten"
        >
          {starting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          {mode || 'Start'}
        </button>
        <a
          href={buildSrc(path)}
          target="_blank"
          rel="noreferrer"
          className="p-1.5 rounded text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
          title="In neuem Tab"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
      {pickMode ? (
        <p className="px-2 py-1 text-[11px] text-sky-300/90 bg-sky-500/10 border-b border-sky-500/20">
          Select-Modus: Element in der Vorschau anklicken → geht in den Chat.
          {hoverLabel ? (
            <span className="ml-2 font-mono text-sky-200/80">{hoverLabel}</span>
          ) : (
            <span className="ml-2 text-neutral-500">Esc zum Abbrechen</span>
          )}
        </p>
      ) : null}
      {error ? (
        <p className="px-2 py-1 text-[11px] text-rose-400 bg-neutral-950">{error}</p>
      ) : null}
      {ticket ? (
        <iframe
          key={frameKey}
          ref={iframeRef}
          title="Agent Live View"
          src={buildSrc(path)}
          className="flex-1 min-h-0 w-full border-0 bg-neutral-950"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 px-4 text-center text-[12px] text-neutral-500 bg-neutral-950">
          {starting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
              <p>Startet Preview…</p>
              <p className="text-[11px] text-neutral-600">Logs erscheinen im Terminal unten.</p>
            </>
          ) : (
            <>
              <p className="text-neutral-300 font-medium">Noch keine Live-Vorschau</p>
              <p className="max-w-sm text-[11px] leading-relaxed text-neutral-500">
                Klicke <span className="font-medium text-neutral-400">Start</span> oben rechts — oder lass den Agent
                <code className="mx-1 text-[10px] px-1 rounded bg-neutral-900 border border-neutral-800">
                  live_starten
                </code>
                aufrufen.
              </p>
              <button
                type="button"
                onClick={() => start({ forceReload: true })}
                className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-[11px] font-medium text-neutral-200 hover:bg-neutral-800"
              >
                <Play className="w-3.5 h-3.5" />
                Preview jetzt starten
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentLiveView;
